/**
 * User Dropdown Component
 *
 * Professional SaaS-style navigation dropdown (Spec 6.3).
 * Shows user info, navigation menu, and admin access (if super_admin).
 *
 * CRITICAL CONSTRAINTS:
 * - TypeScript strict mode (NO 'any' types)
 * - Admin menu item visible only to super_admin users
 * - Auto-close on navigation or outside click
 * - Greens/blues color palette
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';

/**
 * UserDropdown Component
 *
 * Dropdown menu with:
 * - User info header (name + email)
 * - Navigation menu items: Dashboard, Upload, Gallery, Billing
 * - Admin menu item (visible only to super_admin)
 * - Sign out button
 */
export function UserDropdown(): React.JSX.Element {
  const navigate = useNavigate();
  const { currentUser, signOut } = useAuth();
  const { isSuperAdmin } = useUserRole();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  /**
   * Close dropdown when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  /**
   * Handle navigation and close dropdown
   */
  const handleNavigate = (path: string): void => {
    navigate(path);
    setIsOpen(false);
  };

  /**
   * Handle sign out
   */
  const handleSignOut = async (): Promise<void> => {
    try {
      await signOut();
      setIsOpen(false);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  /**
   * Get user initials for avatar
   */
  const getUserInitials = (): string => {
    if (currentUser?.displayName) {
      return currentUser.displayName
        .split(' ')
        .map((name) => name[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    if (currentUser?.email) {
      return currentUser.email[0].toUpperCase();
    }
    return 'U';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
      >
        {/* User Avatar */}
        {currentUser?.photoURL ? (
          <img
            src={currentUser.photoURL}
            alt="User avatar"
            className="w-8 h-8 rounded-full"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center text-white text-sm font-semibold">
            {getUserInitials()}
          </div>
        )}

        {/* Dropdown Arrow */}
        <svg
          className={`w-4 h-4 text-gray-600 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''
            }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-900">
              {currentUser?.displayName || 'User'}
            </p>
            <p className="text-xs text-gray-600 truncate">
              {currentUser?.email}
            </p>
          </div>

          {/* Navigation Menu Items */}
          <div className="py-2">
            {/* Dashboard */}
            <button
              onClick={() => handleNavigate('/dashboard')}
              className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 transition-colors duration-200"
            >
              <span className="text-lg">🏠</span>
              <span>Dashboard</span>
            </button>

            {/* Bulk Upload */}
            <button
              onClick={() => handleNavigate('/upload')}
              className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 transition-colors duration-200"
            >
              <span className="text-lg">📚</span>
              <span>Bulk Upload</span>
            </button>

            {/* Gallery */}
            <button
              onClick={() => handleNavigate('/gallery')}
              className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 transition-colors duration-200"
            >
              <span className="text-lg">🖼️</span>
              <span>Gallery</span>
            </button>

            {/* Billing */}
            <button
              onClick={() => handleNavigate('/billing')}
              className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 transition-colors duration-200"
            >
              <span className="text-lg">💳</span>
              <span>Billing</span>
            </button>

            {/* Admin Menu Item (visible only to super_admin) */}
            {isSuperAdmin && (
              <button
                onClick={() => handleNavigate('/admin')}
                className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-green-50 transition-colors duration-200 font-semibold"
              >
                <span className="text-lg">⚙️</span>
                <span>Admin</span>
              </button>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100 my-2"></div>

          {/* Sign Out */}
          <button
            onClick={handleSignOut}
            className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200"
          >
            <span className="text-lg">🚪</span>
            <span>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
}
